export type Language = 'en' | 'zh' | 'ja';

export const translations = {
  en: {
    // 页面标题和描述
    pageTitle: 'EndlessAI Graph Calculator - endlessai.org',
    pageDescription: 'Online professional graph calculator with real-time rendering and interactive exploration of mathematical functions',

    // UI
    inputPlaceholder: 'Enter e.g.: y = sin(x)',
    calculating: 'Calculating...',
    unknownError: 'Unknown error',
    connectionError: 'Failed to connect to server',

    // Buttons
    gridOptions: 'Grid Options',
    resetView: 'Reset View',
    exportImage: 'Export Image',
    exampleFunctions: 'Example Functions',
    help: 'Help',
    functionList: 'Function List',

    // Function List
    functionListTitle: 'Functions',
    hideFunction: 'Hide function',
    showFunction: 'Show function',
    changeColor: 'Change color',
    removeFunction: 'Remove function',
    pointsCount: '{count} points',
    discontinuitiesCount: '{count} discontinuities',

    // Grid types
    gridNone: 'None',
    gridMajor: 'Major Grid',
    gridMinor: 'Minor Grid',
    gridPolar: 'Polar Grid',
    gridIsometric: 'Isometric Grid',
    gridDots: 'Dot Grid',

    // Example categories
    basicFunctions: 'Basic Functions',
    trigonometric: 'Trigonometric',
    specialFunctions: 'Special Functions',
    compositeFunctions: 'Composite Functions',
    advancedFunctions: 'Advanced Functions',
    multiFunctionExamples: 'Multi-Function Examples',
    advancedEquations: 'Advanced Equations',

    // Example names
    line: 'Line',
    parabola: 'Parabola',
    cubic: 'Cubic Function',
    rational: 'Rational Function',
    sineWave: 'Sine Wave',
    cosineWave: 'Cosine Wave',
    tangent: 'Tangent',
    modulatedWave: 'Modulated Wave',
    lissajous: 'Lissajous Curve',
    dampedOscillation: 'Damped Oscillation',
    gaussian: 'Gaussian Distribution',
    sincFunction: 'Sinc Function',
    stepFunction: 'Step Function',
    sawtoothWave: 'Sawtooth Wave',
    absoluteValue: 'Absolute Value',
    floorFunction: 'Floor Function',
    fourierSeries: 'Fourier Series',
    harmonicSeries: 'Harmonic Series',
    fractalPattern: 'Fractal Pattern',
    quantumWaveFunction: 'Quantum Wave Function',
    compositeTrig: 'Composite Trigonometric',
    gammaFunction: 'Gamma Function',
    errorFunction: 'Error Function',
    besselFunction: 'Bessel Function',
    hyperbolicSine: 'Hyperbolic Sine',
    hyperbolicCosine: 'Hyperbolic Cosine',
    hyperbolicTangent: 'Hyperbolic Tangent',
    sineAndCosine: 'Sine & Cosine',
    parabolas: 'Parabolas',
    exponentials: 'Exponentials',
    inverseFunctions: 'Inverse Functions',
    reverseSine: 'Reverse Sine',
    linearCoefficient: 'Linear Coefficient',
    complexReverse: 'Complex Reverse',
    parametricStyle: 'Parametric Style',


    // 新增类别
        implicitEquations: 'Implicit Equations',
        parametricCurves: 'Parametric Curves',
        polarCurves: 'Polar Curves',
        inequalities: 'Inequalities',

        // 新增示例名称
        circle: 'Circle',
        ellipse: 'Ellipse',
        hyperbola: 'Hyperbola',
        lemniscate: 'Lemniscate',
        foliumOfDescartes: 'Folium of Descartes',
        lissajousCurve: 'Lissajous Curve',
        spiral: 'Spiral',
        cycloid: 'Cycloid',
        astroid: 'Astroid',
        epicycloid: 'Epicycloid',
        rose: 'Rose',
        cardioid: 'Cardioid',
        archimedeanSpiral: 'Archimedean Spiral',
        logarithmicSpiral: 'Logarithmic Spiral',
        butterfly: 'Butterfly Curve',
        halfPlane: 'Half Plane',
        parabolicRegion: 'Parabolic Region',
        circularRegion: 'Circular Region',
        absoluteRegion: 'Absolute Value Region',
        systemOfInequalities: 'System of Inequalities',

    // Help sections
    helpTitle: 'Help',
    enhancedEquationSupport: 'Enhanced Equation Support',
    enhancedEquationSupportDesc: 'Now supports flexible equation formats:',
    standardForm: 'Standard form',
    reverseForm: 'Reverse form',
    linearCoefficientForm: 'Linear coefficient',
    functionDefinition: 'Function definition',
    multiFunctionSupport: 'Multi-Function Support',
    addMultipleFunctions: 'Add multiple functions with the + button',
    manageFunctions: 'Manage functions in the function list panel',
    toggleVisibility: 'Toggle visibility and change colors',
    inputFormat: 'Input Format',
    inputFormatDesc: 'Supports various mathematical expression formats:',
    operations: 'Operations',
    supportedFunctions: 'Supported Functions',
    basicOperations: 'Basic Operations',
    trigFunctions: 'Trigonometric',
    inverseTrig: 'Inverse Trigonometric',
    hyperbolic: 'Hyperbolic',
    exponentialLog: 'Exponential & Logarithmic',
    rootsPowers: 'Roots & Powers',
    rounding: 'Rounding',
    special: 'Special Functions',
    constants: 'Constants',
    gridTypes: 'Grid Types',

    // Operations descriptions
    dragToPan: 'Drag canvas to pan view',
    scrollToZoom: 'Scroll mouse wheel to zoom',
    mobilePinchZoom: 'Mobile: Single finger drag, two finger zoom',
    clickReset: 'Click "Reset View" to restore default view',

    // Grid descriptions
    gridNoneDesc: 'No grid lines',
    gridMajorDesc: 'Standard Cartesian grid',
    gridMinorDesc: 'Grid with subdivisions',
    gridPolarDesc: 'Concentric circles and radial lines',
    gridIsometricDesc: 'Isometric projection grid',
    gridDotsDesc: 'Dots at grid intersections',

    madeBy: 'Made and maintained by',
        endlessaiOrg: 'endlessai.org',
        feedbackWelcome: 'Feedback and suggestions are welcome:',
        email: 'Email',
        wechat: 'WeChat'
  },

  zh: {
    // 页面标题和描述
    pageTitle: '无尽白板图形计算器 - endlessai.org',
    pageDescription: '在线专业图形计算器，支持各种数学函数的实时绘制和交互式探索',

    // UI
    inputPlaceholder: '输入表达式如：y = sin(x)',
    calculating: '计算中...',
    unknownError: '未知错误',
    connectionError: '连接服务器失败',

    // Buttons
    gridOptions: '网格选项',
    resetView: '重置视图',
    exportImage: '导出图片',
    exampleFunctions: '示例函数',
    help: '帮助',
    functionList: '函数列表',

    // Function List
    functionListTitle: '函数',
    hideFunction: '隐藏函数',
    showFunction: '显示函数',
    changeColor: '更改颜色',
    removeFunction: '删除函数',
    pointsCount: '{count} 个点',
    discontinuitiesCount: '{count} 个不连续点',

    // Grid types
    gridNone: '空白',
    gridMajor: '主网格',
    gridMinor: '次网格',
    gridPolar: '极坐标网格',
    gridIsometric: '等距网格',
    gridDots: '点状网格',

    // Example categories
    basicFunctions: '基础函数',
    trigonometric: '三角函数',
    specialFunctions: '特殊函数',
    compositeFunctions: '复合函数',
    advancedFunctions: '高级函数',
    multiFunctionExamples: '多函数示例',
    advancedEquations: '高级方程',

    // Example names
    line: '直线',
    parabola: '抛物线',
    cubic: '立方函数',
    rational: '有理函数',
    sineWave: '正弦波',
    cosineWave: '余弦波',
    tangent: '正切函数',
    modulatedWave: '调制波',
    lissajous: '李萨如图',
    dampedOscillation: '阻尼振荡',
    gaussian: '高斯分布',
    sincFunction: 'Sinc函数',
    stepFunction: '阶跃函数',
    sawtoothWave: '锯齿波',
    absoluteValue: '绝对值',
    floorFunction: '取整函数',
    fourierSeries: '傅里叶级数',
    harmonicSeries: '调和级数',
    fractalPattern: '分形模式',
    quantumWaveFunction: '量子波函数',
    compositeTrig: '复合三角',
    gammaFunction: 'Gamma函数',
    errorFunction: '误差函数',
    besselFunction: '贝塞尔函数',
    hyperbolicSine: '双曲正弦',
    hyperbolicCosine: '双曲余弦',
    hyperbolicTangent: '双曲正切',
    sineAndCosine: '正弦和余弦',
    parabolas: '抛物线组',
    exponentials: '指数函数',
    inverseFunctions: '反函数',
    reverseSine: '反向正弦',
    linearCoefficient: '线性系数',
    complexReverse: '复杂反向',
    parametricStyle: '参数形式',

    // 新增类别
        implicitEquations: '隐式方程',
        parametricCurves: '参数曲线',
        polarCurves: '极坐标曲线',
        inequalities: '不等式',

        // 新增示例名称
        circle: '圆',
        ellipse: '椭圆',
        hyperbola: '双曲线',
        lemniscate: '双纽线',
        foliumOfDescartes: '笛卡尔叶形线',
        lissajousCurve: '李萨如曲线',
        spiral: '螺旋线',
        cycloid: '摆线',
        astroid: '星形线',
        epicycloid: '外摆线',
        rose: '玫瑰线',
        cardioid: '心形线',
        archimedeanSpiral: '阿基米德螺线',
        logarithmicSpiral: '对数螺线',
        butterfly: '蝴蝶曲线',
        halfPlane: '半平面',
        parabolicRegion: '抛物线区域',
        circularRegion: '圆形区域',
        absoluteRegion: '绝对值区域',
        systemOfInequalities: '不等式组',


    // Help sections
    helpTitle: '使用帮助',
    enhancedEquationSupport: '增强方程支持',
    enhancedEquationSupportDesc: '现在支持灵活的方程格式：',
    standardForm: '标准形式',
    reverseForm: '反向形式',
    linearCoefficientForm: '线性系数',
    functionDefinition: '函数定义',
    multiFunctionSupport: '多函数支持',
    addMultipleFunctions: '使用 + 按钮添加多个函数',
    manageFunctions: '在函数列表面板中管理函数',
    toggleVisibility: '切换可见性和更改颜色',
    inputFormat: '输入格式',
    inputFormatDesc: '支持多种数学表达式格式：',
    operations: '操作说明',
    supportedFunctions: '支持的函数',
    basicOperations: '基础运算',
    trigFunctions: '三角函数',
    inverseTrig: '反三角',
    hyperbolic: '双曲函数',
    exponentialLog: '指数对数',
    rootsPowers: '根和幂',
    rounding: '取整',
    special: '特殊函数',
    constants: '常数',
    gridTypes: '网格类型',

    // Operations descriptions
    dragToPan: '拖动画布来平移视图',
    scrollToZoom: '滚动鼠标滚轮来缩放',
    mobilePinchZoom: '移动端：单指拖动，双指缩放',
    clickReset: '点击"重置视图"恢复默认视图',

    // Grid descriptions
    gridNoneDesc: '无网格线',
    gridMajorDesc: '标准笛卡尔坐标网格',
    gridMinorDesc: '带细分的网格',
    gridPolarDesc: '同心圆和径向线',
    gridIsometricDesc: '等角投影网格',
    gridDotsDesc: '网格交点处的点',

    madeBy: '由',
        endlessaiOrg: 'endlessai.org',
        feedbackWelcome: '欢迎提出建议与需求，联系方式：',
        email: '邮箱',
        wechat: '微信'
  },

  ja: {
    // 页面标题和描述
    pageTitle: 'グラフ計算機 - endlessai.org',
    pageDescription: 'オンラインプロフェッショナルグラフ計算機、数学関数のリアルタイム描画とインタラクティブ探索をサポート',

    // UI
    inputPlaceholder: '例を入力：y = sin(x)',
    calculating: '計算中...',
    unknownError: '不明なエラー',
    connectionError: 'サーバーへの接続に失敗しました',

    // Buttons
    gridOptions: 'グリッドオプション',
    resetView: 'ビューをリセット',
    exportImage: '画像をエクスポート',
    exampleFunctions: '関数の例',
    help: 'ヘルプ',
    functionList: '関数リスト',

    // Function List
    functionListTitle: '関数',
    hideFunction: '関数を非表示',
    showFunction: '関数を表示',
    changeColor: '色を変更',
    removeFunction: '関数を削除',
    pointsCount: '{count} ポイント',
    discontinuitiesCount: '{count} 不連続点',

    // Grid types
    gridNone: 'なし',
    gridMajor: '主グリッド',
    gridMinor: '副グリッド',
    gridPolar: '極座標グリッド',
    gridIsometric: '等角グリッド',
    gridDots: 'ドットグリッド',

    // Example categories
    basicFunctions: '基本関数',
    trigonometric: '三角関数',
    specialFunctions: '特殊関数',
    compositeFunctions: '合成関数',
    advancedFunctions: '高度な関数',
    multiFunctionExamples: '多関数例',
    advancedEquations: '高度な方程式',

    // Example names
    line: '直線',
    parabola: '放物線',
    cubic: '三次関数',
    rational: '有理関数',
    sineWave: '正弦波',
    cosineWave: '余弦波',
    tangent: '正接関数',
    modulatedWave: '変調波',
    lissajous: 'リサジュー曲線',
    dampedOscillation: '減衰振動',
    gaussian: 'ガウス分布',
    sincFunction: 'Sinc関数',
    stepFunction: 'ステップ関数',
    sawtoothWave: 'のこぎり波',
    absoluteValue: '絶対値',
    floorFunction: '床関数',
    fourierSeries: 'フーリエ級数',
    harmonicSeries: '調和級数',
    fractalPattern: 'フラクタルパターン',
    quantumWaveFunction: '量子波動関数',
    compositeTrig: '複合三角関数',
    gammaFunction: 'ガンマ関数',
    errorFunction: '誤差関数',
    besselFunction: 'ベッセル関数',
    hyperbolicSine: '双曲線正弦',
    hyperbolicCosine: '双曲線余弦',
    hyperbolicTangent: '双曲線正接',
    sineAndCosine: '正弦と余弦',
    parabolas: '放物線群',
    exponentials: '指数関数',
    inverseFunctions: '逆関数',
    reverseSine: '逆正弦',
    linearCoefficient: '線形係数',
    complexReverse: '複素逆',
    parametricStyle: 'パラメトリック形式',
    // 新增类别
        implicitEquations: '陰関数方程式',
        parametricCurves: 'パラメトリック曲線',
        polarCurves: '極座標曲線',
        inequalities: '不等式',

        // 新增示例名称
        circle: '円',
        ellipse: '楕円',
        hyperbola: '双曲線',
        lemniscate: 'レムニスケート',
        foliumOfDescartes: 'デカルトの葉',
        lissajousCurve: 'リサジュー曲線',
        spiral: '螺旋',
        cycloid: 'サイクロイド',
        astroid: 'アステロイド',
        epicycloid: 'エピサイクロイド',
        rose: 'バラ曲線',
        cardioid: 'カージオイド',
        archimedeanSpiral: 'アルキメデスの螺旋',
        logarithmicSpiral: '対数螺旋',
        butterfly: 'バタフライ曲線',
        halfPlane: '半平面',
        parabolicRegion: '放物線領域',
        circularRegion: '円形領域',
        absoluteRegion: '絶対値領域',
        systemOfInequalities: '不等式系',

    // Help sections
    helpTitle: 'ヘルプ',
    enhancedEquationSupport: '拡張方程式サポート',
    enhancedEquationSupportDesc: '柔軟な方程式形式をサポート：',
    standardForm: '標準形式',
    reverseForm: '逆形式',
    linearCoefficientForm: '線形係数',
    functionDefinition: '関数定義',
    multiFunctionSupport: '多関数サポート',
    addMultipleFunctions: '+ ボタンで複数の関数を追加',
    manageFunctions: '関数リストパネルで関数を管理',
    toggleVisibility: '可視性の切り替えと色の変更',
    inputFormat: '入力形式',
    inputFormatDesc: '様々な数式形式をサポート：',
    operations: '操作方法',
    supportedFunctions: 'サポートされている関数',
    basicOperations: '基本演算',
    trigFunctions: '三角関数',
    inverseTrig: '逆三角関数',
    hyperbolic: '双曲線関数',
    exponentialLog: '指数・対数',
    rootsPowers: '根と累乗',
    rounding: '丸め',
    special: '特殊関数',
    constants: '定数',
    gridTypes: 'グリッドタイプ',

    // Operations descriptions
    dragToPan: 'キャンバスをドラッグして移動',
    scrollToZoom: 'マウスホイールでズーム',
    mobilePinchZoom: 'モバイル：一本指でドラッグ、二本指でズーム',
    clickReset: '「ビューをリセット」をクリックしてデフォルトに戻す',

    // Grid descriptions
    gridNoneDesc: 'グリッドなし',
    gridMajorDesc: '標準デカルト座標グリッド',
    gridMinorDesc: '細分化されたグリッド',
    gridPolarDesc: '同心円と放射線',
    gridIsometricDesc: '等角投影グリッド',
    gridDotsDesc: 'グリッド交点のドット',
    madeBy: '制作・メンテナンス：',
        endlessaiOrg: 'endlessai.org',
        feedbackWelcome: 'ご意見・ご要望をお待ちしております：',
        email: 'メール',
        wechat: 'WeChat'
  }
};

export function getLanguageFromURL(): Language {
  const urlParams = new URLSearchParams(window.location.search);
  const locale = urlParams.get('locale')?.toLowerCase();

  if (locale === 'zh' || locale === 'zh-cn' || locale === 'zh-tw') return 'zh';
  if (locale === 'ja' || locale === 'ja-jp') return 'ja';
  if (locale === 'en' || locale === 'en-us') return 'en';

  // 如果 URL 参数没有指定语言，回退到浏览器语言
  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith('zh')) return 'zh';
  if (browserLang.startsWith('ja')) return 'ja';

  return 'en'; // 默认英语
}

export function getLanguage(): Language {
  return getLanguageFromURL();
}

export function t(key: string, lang?: Language): string {
  const currentLang = lang || getLanguage();
  const langData = translations[currentLang];

  return langData[key as keyof typeof langData] || translations.en[key as keyof typeof translations.en] || key;
}

// 设置页面标题和描述
export function setPageMeta(lang?: Language): void {
  const currentLang = lang || getLanguage();

  document.title = t('pageTitle', currentLang);

  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', t('pageDescription', currentLang));
  }
}